"use client";

import "./page.css";

import { zodResolver } from "@hookform/resolvers/zod";
import { identity, updateProfile, uploadImage } from "deso-protocol";
import Cookies from "js-cookie";
import Image from "next/image";
import { useRouter } from "next/navigation";
import React, {
  type ComponentProps,
  use,
  useEffect,
  useRef,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import { BsTwitterX } from "react-icons/bs";
import {
  LuArrowRight,
  LuBadgeDollarSign,
  LuDollarSign,
  LuLogOut,
  LuSend,
  LuShuffle,
  LuX,
} from "react-icons/lu";
import zod from "zod";

import { waitForTxn } from "@/backend/api-helpers";
import type { GenerateTwitterOauthTokenResponse } from "@/backend/focus";
import {
  authorizeDerivedKeyAndWait,
  authWithTwitter,
  createDefaultMessagingGroup,
  createDerivedKey,
  createFocusProfile,
  createSubscriptionTier,
  DerivedKeyType,
  updatePaidMessagingSettings,
} from "@/backend/focus";
import {
  DialogDrawer,
  DialogDrawerClose,
  DialogDrawerContent,
  DialogDrawerTitle,
  DialogDrawerType,
} from "@/components/shared/DialogDrawer";
import CurrencyInput from "@/components/shared/dollar-input";
import { Spinner } from "@/components/shared/spinner";
import {
  initialAgreed,
  TermsOfService,
} from "@/components/shared/terms-of-service";
import UploadImage from "@/components/shared/upload-image";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { useConfirm } from "@/components/ui/confirm";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/components/ui/use-toast";
import { useLayout } from "@/contexts/layout-context";
import { useOwnTheme } from "@/contexts/theme-context";
import { UserContext } from "@/contexts/user-context";
import { useLogin } from "@/hooks/use-login";
import { useMobile } from "@/hooks/use-mobile";
import { usePushNotificationsPopup } from "@/hooks/use-push-notifications-popup";
import useRefinement from "@/hooks/use-refinement";
import {
  APP_NAME_GENERIC,
  APP_ROUTES,
  DEFAULT_CREATOR_BASIS_POINTS,
  DEFAULT_STAKE_MULTIPLE_BASIS_POINTS,
  DESO_APP_NAME,
  MIN_FEE_RATE_NANOS_PER_KB,
} from "@/utils/constants";
import { CookieKeys } from "@/utils/cookie-keys";
import { dataURIToFile } from "@/utils/helpers";
import { cn } from "@/utils/lib";
import { fetchAndEncodeImage } from "@/utils/media-utils";
import {
  approveCreateTierDerivedKey,
  generateTierAccessGroupKeyName,
} from "@/utils/subscriptions";
import { generateRandomUsername } from "@/utils/usernames";
import { validateUsername } from "@/utils/validators";

const NavButton = ({
  children,
  step,
  text = "Next",
  onClick,
  ...props
}: ComponentProps<typeof Button> & {
  step?: string;
  text: string;
}) => {
  return (
    <Button
      variant="default"
      className="inline-flex items-center gap-2"
      onClick={(ev) => {
        if (step) {
          goTo(step);
        }
        onClick?.(ev);
      }}
      {...props}
    >
      {children || (
        <>
          {text} {step === "finish" ? "" : <LuArrowRight />}
        </>
      )}
    </Button>
  );
};

const goTo = (step: string, smooth: boolean = true) => {
  document.querySelector(`[data-signup-step-id="${step}"]`)?.scrollIntoView({
    behavior: smooth ? "smooth" : "auto",
  });
};

export default function SignupModal({
  params,
}: {
  params: Promise<{ firstStep: string[] }>;
}) {
  const { firstStep = [] } = use(params);
  const router = useRouter();
  const { darkTheme } = useOwnTheme();
  const { history } = useLayout();
  const [agreed, setAgreed] = useState(initialAgreed);
  const allChecked = Object.values(agreed).every(Boolean);
  const [twitterData, setTwitterData] =
    useState<GenerateTwitterOauthTokenResponse | null>(null);
  const {
    user,
    currentUserPublicKey,
    refetchUser,
    refetchFocusProfile,
    loggedIn,
  } = use(UserContext);
  const { login, loading: loginLoading } = useLogin();
  const { toast } = useToast();
  const { isDevice } = useMobile();
  const { showNotificationsPopup } = usePushNotificationsPopup();
  const showNotificationsPopupRef = useRef(showNotificationsPopup);
  const { confirm } = useConfirm();
  const { setFirstPostPopup } = useLayout();

  useEffect(() => {
    showNotificationsPopupRef.current = showNotificationsPopup;
  }, [showNotificationsPopup]);

  const validateUniqueName = useRefinement(
    (username: string) => {
      return validateUsername(username, { currentUsername: user?.username });
    },
    {
      debounce: 300,
    },
  );

  const FormSchema = zod.object({
    minTip: zod
      .number()
      .min(0.01, { message: "Number must be at least 0.01" })
      .max(100, { message: "Number must not exceed 100" }),
    allowFreeMessages: zod.boolean(),
    firstTierName: zod.string().optional(),
    firstTierAmountUSD: zod.coerce.number<number>().or(zod.nan()).optional(),
    username: zod
      .string()
      .min(1, "Username must have at least 1 character")
      .max(25, "Username must have at most 25 characters")
      .refine((value) => /^[a-zA-Z0-9_]*$/.test(value), {
        message: "Must only include alphanumeric or '_'",
      })
      .refine(validateUniqueName, "Username already claimed"),
    displayName: zod.string().min(3, {
      message: "Display name must be at least 3 characters.",
    }),
    avatar: zod.string().optional(),
    cover: zod.string().optional(),
    bio: zod.string().optional(),
  });

  const form = useForm<zod.infer<typeof FormSchema>>({
    mode: "onChange",
    resolver: zodResolver(FormSchema),
    defaultValues: {
      // User
      username: "",
      displayName: "",
      avatar: "",
      cover: "",
      bio: "",

      // Paid dm
      minTip: 0.1,
      allowFreeMessages: true,

      // Paid Subscriptions
      firstTierName: "",
      firstTierAmountUSD: undefined,
    },
  });

  const handleLogin = async () => {
    const loginData = await login();

    // Window was closed, do nothing
    if (!loginData) {
      return;
    }

    await showNotificationsPopupRef.current();

    // Has a Focus account
    if (loginData?.account) {
      if (history.length > 1) {
        router.back();
      } else {
        router.push(APP_ROUTES.app);
      }
    } else {
      goTo("connect-and-earn");
      // It only fails if the user has reloaded the page and it's already created, just ignore it
      createDefaultMessagingGroup(loginData.publicKey!).catch(() => null);
    }
  };

  const onAgreedChange = (key: keyof typeof agreed) => (checked: boolean) => {
    setAgreed((prev) => ({ ...prev, [key]: checked }));
  };

  const randomizeNames = () => {
    const randomName = generateRandomUsername();

    validateUniqueName.invalidate();

    form.setValue("username", randomName, {
      shouldDirty: true,
      shouldValidate: true,
    });
    form.setValue("displayName", randomName, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleConnectTwitter = async () => {
    const jwt = await identity.jwt();

    const callbackUrl = `${process.env.NEXT_PUBLIC_FOCUS_URI}${APP_ROUTES.onboardingTwitterCallback}`;
    const URL = await authWithTwitter({
      jwt,
      publicKey: currentUserPublicKey,
      redirectUrl: callbackUrl,
    });

    if (URL === null) {
      toast({
        variant: "destructive",
        title: "Error",
        description: `An error happened when authenticating with Twitter`,
      });
      return;
    }

    type AuthResults = {
      state: string;
      code: string;
      twitterData: GenerateTwitterOauthTokenResponse;
    } | null;

    // Open twitter login popup
    const authResults = await new Promise<AuthResults>((resolve, reject) => {
      const popup = window.open(URL, "_blank", "width=600,height=700");
      let results: AuthResults = null;

      if (!popup) {
        return reject(new Error("Popup blocked or failed to open"));
      }

      const origin = window.location.origin;
      // eslint-disable-next-line prefer-const
      let intervalId: number;

      // Handle messages from the popup
      function handleMessage(event: MessageEvent) {
        console.log("handleMessage", event);

        if (event.origin !== origin) return;

        if (event.data?.type === "twitter-auth-success") {
          results = event.data.payload; // contains state, code, twitterData
        }
      }

      // Periodically check if the popup is closed
      function checkIfClosed() {
        console.log("checkIfClosed", popup?.closed);
        if (popup?.closed) {
          cleanup();
          resolve(results);
        }
      }

      function cleanup() {
        window.removeEventListener("message", handleMessage);
        clearInterval(intervalId);
      }

      window.addEventListener("message", handleMessage);
      intervalId = window.setInterval(checkIfClosed, 500);
    });

    console.log({ authResults });

    if (!authResults) {
      return;
    }

    const { twitterData } = authResults;
    setTwitterData(twitterData);
    form.setValue("username", twitterData.Username);
    form.setValue("displayName", twitterData.Name);
    form.setValue("bio", twitterData.Description);

    if (twitterData.ProfileImageUrl) {
      form.setValue(
        "avatar",
        await fetchAndEncodeImage(twitterData.ProfileImageUrl),
        {
          shouldValidate: true,
        },
      );
    }

    if (twitterData.ProfileBannerUrl) {
      form.setValue(
        "cover",
        await fetchAndEncodeImage(twitterData.ProfileBannerUrl),
      );
    }

    goTo("create-profile");
  };

  const setupAccount = async () => {
    const firstTierName = form.getValues("firstTierName");
    const firstTierAmountUSD = form.getValues("firstTierAmountUSD") || 0;

    if (firstTierName) {
      const accessGroupKeyName = generateTierAccessGroupKeyName();
      console.log("creating derived key");
      const createDerivedKeyResponse = await createDerivedKey({
        OwnerPublicKey: currentUserPublicKey,
        DerivedKeyType: DerivedKeyType.SUBSCRIPTION_CREATOR,
        AccessGroupOwnerPublicKey: currentUserPublicKey,
        AccessGroupKeyName: accessGroupKeyName,
      });

      if (createDerivedKeyResponse) {
        const derivedPublicKey =
          createDerivedKeyResponse.DerivedPublicKeyBase58Check;

        console.log("approving derived key");
        const approveCreateTierDerivedKeyResponse =
          await approveCreateTierDerivedKey(
            currentUserPublicKey,
            derivedPublicKey,
          );

        console.log("authorizing derived key");
        await authorizeDerivedKeyAndWait({
          OwnerPublicKey: currentUserPublicKey,
          DerivedPublicKey:
            approveCreateTierDerivedKeyResponse.derivedPublicKeyBase58Check,
          ExpirationBlock: approveCreateTierDerivedKeyResponse.expirationBlock,
          AccessSignature: approveCreateTierDerivedKeyResponse.accessSignature,
          TransactionSpendingLimitHex:
            approveCreateTierDerivedKeyResponse.transactionSpendingLimitHex,
        });

        console.log("creating subscription tier");
        const createResp = await createSubscriptionTier({
          CreatorPublicKey: currentUserPublicKey,
          DerivedPublicKey: derivedPublicKey,
          AccessGroupKeyName: accessGroupKeyName,
          Name: firstTierName,
          CostUsdCents: firstTierAmountUSD * 100,
          PaymentCadenceDays: 30,
          CoverImageURL: "",
          Description: "",
          E2E: false,
          AccessGroupPublicKey: "",
        });

        if (!createResp.TxnHashHex) {
          throw new Error("TxnHashHex not found in response.");
        }

        console.log("waiting for txn");
        await waitForTxn({
          transactionHash: createResp.TxnHashHex,
        });
      }
    }

    console.log("creating focus profile");
    await createFocusProfile(
      currentUserPublicKey,
      Cookies.get(CookieKeys.password),
    );

    // eslint-disable-next-line prefer-const
    let [username, displayName, avatar = "", cover = "", bio = ""] =
      form.getValues(["username", "displayName", "avatar", "cover", "bio"]);

    if (cover) {
      const file = await dataURIToFile(cover, "cover");

      const uploadResponse = await uploadImage(
        {
          file,
          UserPublicKeyBase58Check: currentUserPublicKey,
        },
        {
          nodeURI: process.env.NEXT_PUBLIC_CORE_DESO_NODE,
        },
      );

      cover = uploadResponse.ImageURL;
    }

    let avatarImageUrl = "";
    if (avatar) {
      const file = await dataURIToFile(avatar, "avatar");

      const uploadResponse = await uploadImage(
        {
          file,
          UserPublicKeyBase58Check: currentUserPublicKey,
        },
        {
          nodeURI: process.env.NEXT_PUBLIC_CORE_DESO_NODE,
        },
      );

      avatarImageUrl = uploadResponse.ImageURL;
    }

    const fee = form.getValues("minTip") * 100;
    console.log("updating paid messaging settings");
    await updatePaidMessagingSettings({
      FeePerMessageUsdCents: fee,
      FollowingFeePerMessageUsdCents: form.getValues("allowFreeMessages")
        ? 0
        : fee,
      UserPublicKeyBase58: currentUserPublicKey,
      Version: 0,
    });

    console.log("updating profile");
    const updateProfileResponse = await updateProfile({
      ProfilePublicKeyBase58Check: currentUserPublicKey,
      UpdaterPublicKeyBase58Check: currentUserPublicKey,
      NewProfilePic: avatar,
      NewUsername: username,
      MinFeeRateNanosPerKB: MIN_FEE_RATE_NANOS_PER_KB,
      NewDescription: bio,
      NewCreatorBasisPoints: user?.creatorBasisPoints
        ? Number(user.creatorBasisPoints)
        : DEFAULT_CREATOR_BASIS_POINTS,
      NewStakeMultipleBasisPoints: DEFAULT_STAKE_MULTIPLE_BASIS_POINTS,
      IsHidden: false,
      ExtraData: {
        DisplayName: displayName,
        LargeProfilePicURL: avatarImageUrl,
        FeaturedImageURL: cover,
      },
    });

    if (!updateProfileResponse.submittedTransactionResponse?.TxnHashHex) {
      return Promise.reject(
        "Transaction was not constructed. Please try again.",
      );
    }

    console.log("waiting for txn");
    await waitForTxn({
      transactionHash:
        updateProfileResponse.submittedTransactionResponse?.TxnHashHex,
    });

    console.log("refetching user");
    refetchUser();
    refetchFocusProfile();

    setFirstPostPopup(true);

    goTo("welcome");
  };

  useEffect(() => {
    if (user?.username) {
      form.setValue("username", user.username, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
    if (user?.description) {
      form.setValue("bio", user.description, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [form, user]);

  const steps = [
    {
      id: "create-wallet",
      title: "",
      content: (
        <div
          className="
            mb-0 text-center text-sm/6 text-foreground
            md:text-base
          "
        >
          <Image
            src={
              darkTheme ? "/assets/logo-deso.svg" : "/assets/logo-deso-dark.svg"
            }
            alt="DeSo Logo"
            width={120}
            height={0}
            className="mx-auto mb-8"
          />

          <p className="mb-4 text-balance">
            The first step is to create a{" "}
            <span className="text-primary">
              Decentralized Social ({DESO_APP_NAME}) wallet.
            </span>
          </p>

          <p className="leading-6 text-balance">
            Your {DESO_APP_NAME} wallet represents your decentralized identity,
            social graph and content so you can't be deplatformed. It also holds
            your assets (money, tokens, NFTs, etc).
          </p>

          <div className="m-auto mt-8 flex flex-col items-center justify-center">
            <TermsOfService
              agreed={agreed}
              onAgreedChange={onAgreedChange}
              labelClassName="text-sm mx-auto peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
              wrapperClassName="max-w-fit"
            />
            <Button
              onClick={handleLogin}
              disabled={!allChecked || loginLoading}
            >
              {loginLoading ? "Loading..." : "Create or Import Wallet"}
            </Button>
          </div>
        </div>
      ),
    },
    {
      id: "connect-and-earn",
      title: "Verify Your Account",
      logout: true,
      footer: (
        <NavButton
          step="create-profile"
          text={twitterData ? "Continue" : "Skip"}
        />
      ),
      content: (
        <>
          <div
            className="
              mb-4 flex flex-col gap-y-2 text-sm/6 text-balance text-foreground
              md:text-base
            "
          >
            <p>
              Connect your Twitter /
              <BsTwitterX className="relative -top-px ml-1 inline-block size-4" />{" "}
              account to{" "}
              <span className="font-semibold text-muted-foreground">
                prefill your profile data.
              </span>
            </p>
          </div>
          <div className="w-full rounded-full border border-border bg-accent">
            <section
              className="
                border-b border-border-light p-2 pl-4 text-left
                last:border-b-0
              "
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-0">
                  <label>
                    <div className="flex items-center gap-3">
                      <span className="text-lg text-muted">
                        <BsTwitterX />
                      </span>
                      <span className="text-muted-foreground">Twitter / X</span>
                    </div>
                  </label>
                </div>

                <div>
                  <Button
                    variant="outlinePrimary"
                    className="border border-primary!"
                    onClick={handleConnectTwitter}
                    disabled={!!twitterData}
                  >
                    {twitterData ? "Connected" : "Connect"}
                  </Button>
                </div>
              </div>
            </section>
          </div>
          {/*Just for debugging purposes*/}
          {/*{twitterData && <pre>{JSON.stringify(twitterData, null, 2)}</pre>}*/}
        </>
      ),
    },
    {
      id: "create-profile",
      title: "Create Profile",
      logout: true,
      footer: (
        <NavButton
          step="earn"
          text="Continue"
          disabled={
            !form.getValues("username").length ||
            !form.getValues("displayName").length ||
            form.getFieldState("username").invalid ||
            form.getFieldState("displayName").invalid
          }
        />
      ),
      content: (
        <div className="flex w-full max-w-[550px] flex-col gap-4">
          <div
            className="
              mb-4 text-sm text-balance text-foreground
              md:text-base
            "
          >
            Create the last social account you'll ever need.{" "}
            <span className="font-semibold text-muted-foreground">
              Your profile, followers, content &amp; assets will be the same
              across every social platform that uses DeSo.
            </span>
          </div>
          <div
            className="
            flex w-full flex-col rounded-lg border border-border p-4
          "
          >
            <div
              className="
                flex flex-col gap-4
                md:flex-row
              "
            >
              <div
                className="
                  flex w-full flex-col justify-center rounded-lg text-center
                  text-sm text-muted
                  md:w-auto
                "
              >
                <UploadImage
                  user={user}
                  imgSize={isDevice ? 70 : 90}
                  onImageSelected={(_, image) => {
                    form.setValue("avatar", image);
                  }}
                  defaultImage={form.getValues("avatar") || undefined}
                />
              </div>
              <div className="flex w-full flex-col gap-4">
                <div className="relative">
                  <FormField
                    control={form.control}
                    name="username"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <TooltipProvider>
                          <Tooltip open={fieldState.invalid}>
                            <TooltipTrigger className="w-full">
                              <FormControl>
                                <Input
                                  className="border border-border"
                                  placeholder="Username"
                                  {...field}
                                  onChange={(e) => {
                                    validateUniqueName.invalidate();
                                    field.onChange(e);
                                  }}
                                />
                              </FormControl>
                            </TooltipTrigger>
                            <TooltipContent align="end">
                              <FormMessage />
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </FormItem>
                    )}
                  />
                  <div
                    className="
                      absolute inset-y-0 right-0 flex items-center
                      justify-center gap-2 p-4
                    "
                  >
                    <LuShuffle className="text-muted" />
                    <p
                      className="
                        cursor-pointer text-xs text-muted underline
                        underline-offset-4
                        hover:text-primary
                      "
                      onClick={randomizeNames}
                    >
                      Randomize
                    </p>
                  </div>
                </div>
                <div>
                  <FormField
                    control={form.control}
                    name="displayName"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            className="border border-border"
                            placeholder="Display name"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "earn",
      title: "Setup Monetization",
      logout: true,
      content: (
        <div className="flex flex-col gap-6">
          <div
            className="
              text-sm text-balance text-foreground
              md:text-base
            "
          >
            Choose how you want to{" "}
            <span className="font-semibold text-muted-foreground">
              earn money on {APP_NAME_GENERIC} from day one, with no
              restrictions.
            </span>
          </div>
          <Tabs defaultValue="paid-dms" className="w-full">
            <TabsList
              className="
                flex w-full justify-around gap-4 rounded-lg border-x p-2
              "
            >
              <TabsTrigger value="paid-dms" className="group">
                <LuSend className="mr-2 size-4" />{" "}
                <span
                  className="
                    hidden
                    group-data-active:inline
                    md:inline
                  "
                >
                  Paid Messages (DMs)
                </span>
              </TabsTrigger>
              <TabsTrigger value="subscription-tier" className="group">
                <LuBadgeDollarSign className="mr-2 size-4" />{" "}
                <span
                  className="
                    hidden
                    group-data-active:inline
                    md:inline
                  "
                >
                  Paid Subscriptions
                </span>
              </TabsTrigger>
            </TabsList>
            <TabsContent value="paid-dms" className="mt-4">
              <div
                className="
                  flex flex-col rounded-lg border bg-accent p-4
                  md:p-8
                "
              >
                <div className="flex w-full">
                  <FormField
                    control={form.control}
                    name="minTip"
                    render={() => (
                      <FormItem
                        className="
                          flex w-full flex-col items-center justify-between
                          md:flex-row md:items-start
                        "
                      >
                        <FormLabel
                          className="
                            mb-4 flex flex-col gap-2 leading-6
                            md:text-left
                          "
                        >
                          Set a minimum tip to reach your inbox
                          <p
                            className="
                              hidden text-sm font-normal text-balance text-muted
                              md:block
                            "
                          >
                            All other messages will go to your spam inbox.
                            Messages are fully end-to-end encrypted, on-chain.
                          </p>
                        </FormLabel>
                        <FormControl>
                          <CurrencyInput
                            className="mb-4 w-auto max-w-[100px]"
                            suffix={<LuDollarSign />}
                            name="minTip"
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
                <div
                  className="
                    unforce-radius flex flex-col gap-4 border-t pt-4
                    md:mt-2
                  "
                >
                  <FormField
                    control={form.control}
                    name="allowFreeMessages"
                    render={({ field }) => {
                      return (
                        <FormItem
                          className="
                            relative
                            md:pt-2
                          "
                        >
                          <div
                            className="
                              flex items-start justify-center gap-2
                              md:justify-start
                            "
                          >
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                id="allowFreeMessages"
                              />
                            </FormControl>
                            <FormLabel
                              htmlFor="allowFreeMessages"
                              className="
                                inline-flex basis-[167px] cursor-pointer
                                flex-wrap gap-1 leading-5
                              "
                            >
                              Allow free messages{" "}
                              <span className="text-muted">
                                (from people you follow)
                              </span>
                            </FormLabel>
                          </div>
                        </FormItem>
                      );
                    }}
                  />
                </div>
              </div>
            </TabsContent>
            <TabsContent value="subscription-tier" className="mt-4">
              <div
                className="
                  flex flex-col gap-4 rounded-2xl border bg-accent p-4 text-left
                "
              >
                <div>
                  <FormField
                    control={form.control}
                    name="firstTierName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel
                          className="
                            mb-4 flex flex-col gap-2 text-left leading-6
                          "
                        >
                          First Tier Name
                        </FormLabel>
                        <FormControl>
                          <Input
                            className="border border-border"
                            placeholder="Premium, Basic, etc."
                            {...field}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
                <div>
                  <FormField
                    control={form.control}
                    name="firstTierAmountUSD"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel
                          className="
                            mb-4 flex flex-col gap-2 text-left leading-6
                          "
                        >
                          Price per Month ($USD)
                        </FormLabel>
                        <FormControl>
                          <CurrencyInput
                            placeholder="0.99, 4.99, 10, etc."
                            min="0"
                            suffix={<LuDollarSign />}
                            {...field}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      ),
      footer: (
        <NavButton
          step="finish"
          text="Done"
          onClick={setupAccount}
          disabled={
            form.getFieldState("minTip").invalid ||
            form.getFieldState("firstTierAmountUSD").invalid
          }
        />
      ),
    },
    {
      id: "finish",
      title: "Setting up your account...",
      content: <Spinner size={28} />,
    },
    {
      id: "welcome",
      title: "",
      footer: (
        <NavButton
          text="Get Started"
          onClick={() => {
            if (history.length > 1) {
              router.back();
            } else {
              router.push(APP_ROUTES.app);
            }
          }}
        />
      ),
      content: (
        <>
          <div
            className="
              mb-2 flex flex-col items-center gap-2
              md:mb-8
            "
          >
            <Image
              src="/assets/logo-focus-whale.svg"
              alt="Coins"
              width={50}
              height={0}
              className="mb-2"
            />
            <h4
              className="
                flex items-center gap-2 font-sans text-lg font-semibold
                text-muted-foreground
                md:text-xl
              "
            >
              Start exploring a whole new way to grow your net worth.
            </h4>
            <p
              className="
                hidden text-sm/6 text-balance text-muted
                md:block md:text-base
              "
            >
              Focus is a social network that allows you to trade and launch
              coins, and earn money from your content.
            </p>
          </div>
          <div
            className="
              mb-6 flex flex-col gap-6
              md:flex-row
            "
          >
            <div className="flex flex-col items-center gap-2">
              <Image
                src="/assets/empty-14.png"
                alt="Coins"
                width={100}
                height={0}
                className="
                  mb-2 hidden
                  md:block
                "
              />
              <h4
                className="
                  hidden text-base font-semibold text-muted-foreground
                  md:block
                "
              >
                Manage Your Feed
              </h4>
              <p className="text-sm text-balance text-muted">
                On Focus, you control your own feed &amp; algorithm.
              </p>
              <Button variant="outline" size="sm">
                Browse Feeds
              </Button>
            </div>
            <div className="flex flex-col items-center gap-3">
              <Image
                src="/assets/empty-6.png"
                alt="Coins"
                width={100}
                height={0}
                className="
                  mb-2 hidden
                  md:block
                "
              />
              <h4
                className="
                  hidden text-base font-semibold text-muted-foreground
                  md:block
                "
              >
                Trade & Launch Coins
              </h4>
              <p className="text-sm text-muted">
                Create your own token, or trade other people's.
              </p>
              <Button variant="outline" size="sm">
                Start Trading
              </Button>
            </div>
            <div className="flex flex-col items-center gap-3">
              <Image
                src="/assets/empty-17.png"
                alt="Coins"
                width={100}
                height={0}
                className="
                  mb-2 hidden
                  md:block
                "
              />
              <h4
                className="
                  hidden text-base font-semibold text-muted-foreground
                  md:block
                "
              >
                Connect with friends
              </h4>
              <p className="text-sm text-muted">
                Discover new people, and connect with friends.
              </p>
              <Button variant="outline" size="sm">
                Discover Users
              </Button>
            </div>
          </div>
        </>
      ),
    },
  ];

  return (
    <DialogDrawer
      open
      onOpenChange={() => router.back()}
      type={DialogDrawerType.Dialog}
    >
      <DialogDrawerContent
        className="
          unforce-radius h-screen w-full max-w-full rounded-none border-0
          outline-none
          md:max-h-[800px] md:w-[1200px] md:max-w-[96vw] md:rounded-lg md:border
        "
        onInteractOutside={(ev) => ev.preventDefault()}
        ref={(el) => {
          if (loggedIn && el && firstStep?.[0] && !el.dataset.scrolled) {
            el.dataset.scrolled = "true";
            goTo(firstStep[0], false);
          }
        }}
        closeClassName="hidden"
      >
        <DialogDrawerTitle className="hidden">Signup/Login</DialogDrawerTitle>
        <Form {...form}>
          <div
            className={cn(
              // This enables scrollbars for testing
              //"w-full h-full bg-background scroll-container custom-scrollbar flex flex-col gap-4",
              `
                scroll-container flex size-full flex-col gap-4 overflow-hidden
                bg-background
              `,
            )}
          >
            {steps.map((step, index) => (
              <Card
                key={step.id}
                className={cn(
                  `
                    scroll-card custom-scrollbar flex min-h-full flex-col
                    justify-center overflow-auto
                  `,
                )}
                data-signup-step-id={step.id}
              >
                <CardContent
                  className="
                    relative flex grow flex-col justify-center bg-background p-4
                    md:p-8
                  "
                >
                  <div
                    className="
                      mx-auto flex max-w-[600px] flex-col items-center
                      justify-center gap-4 text-center
                    "
                  >
                    {step.title && (
                      <h4
                        className="
                          flex items-center gap-2 font-sans text-xl
                          font-semibold text-primary
                        "
                      >
                        {step.title}
                      </h4>
                    )}
                    {step.content}
                  </div>
                  <div
                    className="
                      mx-auto mt-4 flex w-auto flex-col gap-4
                      md:mt-8
                    "
                  >
                    {step.footer}
                  </div>
                </CardContent>
                {index === 0 && (
                  <DialogDrawerClose
                    className={cn(
                      buttonVariants({
                        variant: "outline",
                        size: "sm",
                      }),
                      "absolute top-6 right-6",
                    )}
                  >
                    <LuX />
                  </DialogDrawerClose>
                )}
                {index >= 1 && index <= 3 && (
                  <div
                    className="
                      absolute inset-x-0 bottom-[30px] mx-auto hidden w-auto
                      items-center justify-center gap-4 text-center
                      md:flex
                    "
                  >
                    <div
                      className={cn(
                        "size-2 rounded-full bg-muted/50",
                        step.id == "connect-and-earn" ? "bg-primary" : "",
                      )}
                    />
                    <div
                      className={cn(
                        "size-2 rounded-full bg-muted/50",
                        step.id == "create-profile" ? "bg-primary" : "",
                      )}
                    />
                    <div
                      className={cn(
                        "size-2 rounded-full bg-muted/50",
                        step.id == "earn" ? "bg-primary" : "",
                      )}
                    />
                  </div>
                )}
                {step.logout && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="absolute top-4 right-4 gap-2 text-sm"
                    onClick={async () => {
                      const confirmed = await confirm({
                        title: "Logout",
                        description:
                          "Are you sure you want to logout? This will cancel your onboarding. You can continue by logging in again.",
                        primaryCTA: "Logout",
                        cancelCTA: "Cancel",
                      });
                      if (!confirmed) return;

                      await identity.logout();

                      if (history.length > 1) {
                        router.back();
                      } else {
                        router.push(APP_ROUTES.app);
                      }
                    }}
                  >
                    <LuLogOut />
                    Logout
                  </Button>
                )}
              </Card>
            ))}
          </div>
        </Form>
      </DialogDrawerContent>
    </DialogDrawer>
  );
}
